import { Box } from '@mui/material'
import { FC } from 'react'
import { Datagrid, DatagridProps, FunctionField } from 'react-admin'
import { Muted } from '../../components/ui'
import { themeColor } from '../../layout/theme/color'
import { monoFontFamily } from '../../layout/theme/semanticTokens'
import { CertificateOut } from '../../types/apiRequests'
import { formatShortDate } from '../../utils/dateFormatter'
import { getInstance } from '../../utils/getInstance'
import { getKoppelvlak } from '../../utils/getKoppelvlak'
import { CertificateStatusPill, CertificateValidityPill } from './CertificatePills'
import { useCertificateLabels } from './targetSystems'

/** Main column: hostname, with "Zaken · PKI" below. */
export const CertificateTitle: FC<{ record: CertificateOut }> = ({ record }) => {
  const { system, type, title } = useCertificateLabels(record)
  return (
    <Box>
      <Box sx={{ fontWeight: 600, color: themeColor.slate }}>{title}</Box>
      <Box sx={{ fontFamily: monoFontFamily, fontSize: 12, color: themeColor.slate50 }}>
        {[system, type].filter(Boolean).join(' · ')}
      </Box>
    </Box>
  )
}

/** Koppeling: interface + environment where the target system has them (Zaken), else a dash. */
export const CertificateLink: FC<{ record: CertificateOut }> = ({ record }) => {
  const koppelvlak = getKoppelvlak(record)
  const instance = getInstance(record)
  if (!koppelvlak && !instance) return <Muted>—</Muted>
  return (
    <Box>
      <Box>{koppelvlak || '—'}</Box>
      {instance && <Box sx={{ fontSize: 12.5 }}><Muted mono>{instance}</Muted></Box>}
    </Box>
  )
}

/** The certificate columns, for the main list (inside <List>) and for fixed arrays (inside a ListContextProvider). */
export const CertificateDatagrid: FC<Pick<DatagridProps, 'rowClick'>> = ({ rowClick }) => (
  <Datagrid bulkActionButtons={false} rowClick={rowClick}>
    <FunctionField<CertificateOut>
      label="resources.certificates.fields.certificate"
      cellClassName="col-main"
      render={(record) => <CertificateTitle record={record} />}
    />
    <FunctionField<CertificateOut>
      label="resources.certificates.fields.link"
      cellClassName="col-nowrap"
      render={(record) => <CertificateLink record={record} />}
    />
    <FunctionField<CertificateOut>
      label="resources.certificates.fields.status"
      cellClassName="col-nowrap"
      render={() => <CertificateStatusPill />}
    />
    <FunctionField<CertificateOut>
      label="resources.certificates.fields.geldigheid"
      cellClassName="col-nowrap"
      render={() => <CertificateValidityPill />}
    />
    {/* source: keeps the main list sortable by creation date. */}
    <FunctionField<CertificateOut>
      source="created"
      label="resources.certificates.fields.created"
      cellClassName="col-date"
      render={(record) => formatShortDate(record.created)}
    />
  </Datagrid>
)
